import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Formik, Form, Field } from "formik";
import { useMediaQuery } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useParams, useLocation, useHistory } from "react-router-dom";
// component
import AuthService from "Lib/API/Auth";
import { useApiAction } from "hook/useFetchData";
import { getLocalStorageItem } from "utils/functions";
import { setLogoImage } from "services/api/organization";
import { getAccessTokenSilently, setLocalStorageItem } from "utils/functions";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const OtpSchema = Yup.object().shape({
  otp: Yup.array()
    .of(
      Yup.string()
        .matches(/^[0-9]$/, "Must be a digit")
        .required("Required")
    )
    .min(6, "Enter 6 digits"),
});

const VerifyOtp = () => {
  const { id } = useParams();
  const inputRefs = useRef([]);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const orgName = getLocalStorageItem("orgName");
  const isSmallScreen = useMediaQuery("(max-width:699px)");
  const logo = getLocalStorageItem("getOrginasitionBySubDomain", true);
  //
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");
  const sentMail = queryParams.get("sentMail");
  // state
  const [coolDown, setCoolDown] = useState(0);
  const [loading, setIsLoading] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  // api
  const { execute: otpVerify } = useApiAction(
    (body) => AuthService.otpVerify(body),
    false
  );
  const { execute: sentCode } = useApiAction(
    (body) => AuthService.loginWithOtp(body),
    false
  );

  const handleChange = (e, index, setFieldValue) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setFieldValue(`otp[${index}]`, value);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index, values, setFieldValue) => {
    if (e.key === "Backspace") {
      if (values.otp[index]) {
        setFieldValue(`otp[${index}]`, "");
      } else if (index > 0) {
        inputRefs.current[index - 1].focus();
        setFieldValue(`otp[${index - 1}]`, "");
      }
    }
  };

  const requestOtp = async () => {
    setIsLoading(true);
    try {
      const res = await sentCode({
        email: sentMail,
      });
      history.replace(
        `/verify-otp/${res?.result?.token}?email=${
          res?.result?.email
        }&&sentMail=${encodeURIComponent(sentMail)}`
      );
    } catch (error) {
      setErrorMessage(error?.data?.message);
      setTimeout(() => {
        setErrorMessage(null);
      }, 7000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (coolDown > 0) {
      const timer = setInterval(() => {
        setCoolDown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [coolDown]);

  useEffect(() => {
    setResendCount(0);
    setCoolDown(30);
  }, []);

  const handleResend = () => {
    requestOtp();
    setResendCount((prev) => prev + 1);
    if (resendCount === 0) {
      setCoolDown(60); // after first, wait 1min
    } else {
      setCoolDown(60);
    }
  };

  return (
    <Box className="login-wrapper">
      <Box
        sx={{
          width: {
            xs: "95%",
            sm: 400,
            md: 450,
          },
          boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
          backgroundColor: "white",
          p: 3,
          borderRadius: "5px",
        }}
      >
        <Box className="login-logo">
          {logo?.logoImg && (
            <img src={logo?.logoImg} alt="logo" loading="lazy" width={160} />
          )}
        </Box>
        <Typography
          variant="h6"
          fontWeight="bold"
          align="center"
          sx={{ fontSize: isSmallScreen ? Fonts.GeneralSubHeaderFont.fontSize : Fonts.GeneralHeaderFont.fontSize }}
        >
          {orgName}
        </Typography>

        <Box>
          <Typography
            variant="body2"
            align="center"
            sx={{
              mb: 2,
              mt: 1,
              fontSize: isSmallScreen ? Fonts.GeneralBodyFont.fontSize : Fonts.GeneralLargeBodyFont.fontSize,
              color: FontColors.BlackAlpha54,
            }}
          >
            To access your membership portal, please enter the code sent to your
            email: <br />
            <Box sx={{ fontWeight: FontWeights.SemiBold }}>{email}</Box>
          </Typography>

          <Formik
            initialValues={{ otp: ["", "", "", "", "", ""] }}
            validationSchema={OtpSchema}
            onSubmit={async (values) => {
              const otpValue = values.otp.join("");
              const body = {
                code: otpValue,
                token: id,
              };
              try {
                const redirectUrl = localStorage.getItem("redirectUrl");
                const res = await otpVerify(body);
                setLocalStorageItem("userAuthData", res?.result);
                toastr.success("Success", "Login successfully! Redirecting...");

                const token = await getAccessTokenSilently();
                AuthService.getUserExist(token)
                  .then((res) => {
                    localStorage.setItem(
                      "paymentMethod",
                      JSON.stringify(res?.user?.paymentMethodCreditCard)
                    );
                    localStorage.setItem(
                      "checkUser",
                      JSON.stringify(res?.exists)
                    );
                    localStorage.setItem(
                      "accountID",
                      res?.user?.permissions?.linked_account?._id
                    );
                    localStorage.setItem(
                      "accountName",
                      res?.user?.permissions?.linked_account?.family_name
                    );
                    localStorage.setItem(
                      "accountInfo",
                      JSON.stringify({
                        firstName:
                          res?.user?.permissions?.linked_contact?.first_name,
                        lastName:
                          res?.user?.permissions?.linked_contact?.last_name,
                        primary_email_address:
                          res?.user?.permissions?.linked_contact?.email_address,
                        emergency_contact_name:
                          res?.user?.permissions?.linked_contact
                            ?.emergency_contact_name,
                        emergency_contact_number:
                          res?.user?.permissions?.linked_contact
                            ?.emergency_contact_number,
                        profileImage:
                          res?.user?.permissions?.linked_contact?.profile_img,
                      })
                    );
                    localStorage.setItem(
                      "organization",
                      JSON.stringify(res?.user?.permissions?.organization)
                    );
                    localStorage.setItem(
                      "memberSetting",
                      JSON.stringify(
                        res?.user?.permissions?.organization?.memberSetting ||
                          {}
                      )
                    );
                    localStorage.setItem(
                      "memberPortalDirectory",
                      JSON.stringify(
                        res?.user?.permissions?.organization
                          ?.memberPortalDirectory || false
                      )
                    );
                    localStorage.setItem(
                      "linkedAccount",
                      JSON.stringify(res?.user?.permissions?.linked_account)
                    );
                    localStorage.setItem("hasPayment", res?.user?.hasPayment);
                    localStorage.setItem(
                      "logoImg",
                      res?.user?.permissions?.organization?.logoImg
                    );
                    localStorage.setItem(
                      "orgId",
                      res?.user?.permissions?.organization?._id
                    );
                    res?.user?.permissions?.organization?.logoImg &&
                      dispatch(
                        setLogoImage(
                          res?.user?.permissions?.organization?.logoImg
                        )
                      );
                    localStorage.setItem(
                      "orgName",
                      res?.user?.permissions?.organization?.orgName
                    );
                    document.title = `${res?.user?.permissions?.organization?.orgName}`;
                  })
                  .catch((err) => {
                    localStorage.setItem("checkUser", err?.data?.exists);
                    toastr.error("Error", err?.data?.message);
                  });

                setTimeout(() => {
                  history.push(redirectUrl ? redirectUrl : "/");
                }, 100);
              } catch (error) {
                setErrorMessage(error?.data?.message);
                setTimeout(() => {
                  setErrorMessage(null);
                }, 7000);
              } finally {
              }
            }}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form>
                <Box
                  display="flex"
                  justifyContent="center"
                  sx={{
                    gap: {
                      xs: 0.5,
                      sm: 1,
                      md: 1.5,
                    },
                  }}
                  mb={2}
                >
                  {values?.otp?.map((val, index) => (
                    <Field key={index} name={`otp[${index}]`}>
                      {() => (
                        <TextField
                          inputRef={(el) => (inputRefs.current[index] = el)}
                          value={val}
                          onChange={(e) =>
                            handleChange(e, index, setFieldValue)
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, index, values, setFieldValue)
                          }
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData
                              .getData("text")
                              .replace(/\D/g, ""); // digits only
                            if (!pasted) return;

                            const otpArray = [...values.otp];
                            for (
                              let i = 0;
                              i < pasted.length && index + i < 6;
                              i++
                            ) {
                              otpArray[index + i] = pasted[i];
                            }
                            otpArray.forEach((digit, i) =>
                              setFieldValue(`otp[${i}]`, digit)
                            );
                            // focus the last filled box
                            const lastIndex = Math.min(
                              index + pasted.length - 1,
                              5
                            );
                            inputRefs.current[lastIndex].focus();
                          }}
                          inputProps={{
                            maxLength: 1,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            style: {
                              textAlign: "center",
                              fontSize: Fonts.GeneralHeaderFont.fontSize,
                              fontWeight: Fonts.GeneralHeaderFont.fontWeight,
                              width: "45px",
                            },
                          }}
                          variant="outlined"
                          error={
                            touched.otp &&
                            Boolean(errors.otp) &&
                            values.otp[index] === ""
                          }
                        />
                      )}
                    </Field>
                  ))}
                </Box>
                {((touched.otp && errors.otp) || errorMessage) && (
                  <Typography
                    color="error"
                    variant="caption"
                    align="center"
                    display="block"
                    sx={{ fontSize: Fonts.GeneralLargeBodyFont.fontSize }}
                  >
                    {errorMessage ? errorMessage : "OTP Required"}
                  </Typography>
                )}

                <Box
                  sx={{
                    flexDirection: "column",
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      textTransform: "none",
                      fontWeight: Fonts.GeneralSubHeaderFont.fontWeight,
                      backgroundColor: "#1675e0",
                      width: "100%",
                      fontSize: Fonts.GeneralSubHeaderFont.fontSize,
                    }}
                    disabled={isSubmitting}
                  >
                    Verify
                    {isSubmitting && (
                      <CircularProgress
                        sx={{ color: FontColors.Blue1976D2, ml: 1 }}
                        size={15}
                        thickness={4}
                      />
                    )}
                  </Button>
                  <span
                    className="login-back"
                    onClick={() => !isSubmitting && history.push("/login")}
                  >
                    Back to login
                  </span>

                  <Button
                    variant="text"
                    disabled={coolDown > 0 || loading || isSubmitting}
                    onClick={handleResend}
                    sx={{ textTransform: "none", fontSize: Fonts.GeneralButtonFont.fontSize, mt: 1 }}
                  >
                    {coolDown > 0
                      ? `Resend code in ${coolDown}s`
                      : "Resend Code"}
                  </Button>
                </Box>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    mt: 1,
                    fontSize: isSmallScreen ? Fonts.GeneralCaptionFont.fontSize : Fonts.GeneralLargeBodyFont.fontSize,
                    color: FontColors.BlackAlpha54,
                  }}
                >
                  By logging in, you agree to our terms of service and privacy
                  policy.
                </Typography>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyOtp;
